
import {
  defineComponent, onMounted, ref, watch, computed,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import SegmentationSimpleProperty from '@/components/segmentations/SegmentationSimpleProperty.vue';
import SegmentationComplexProperty from '@/components/segmentations/SegmentationComplexProperty.vue';
import OverrideInfoPanel from '@/components/segmentations/OverrideInfoPanel.vue';
import { store } from '@/store';
import PropertyDeletePanel from '@/components/segmentations/PropertyDeletePanel.vue';
import validateSegmentProperty from '@/components/segmentations/utils/utils';
import useProductOverrides from '@/composables/segmentations/useProductOverrides';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { TemplateParentTypeEnum } from '@/types';

export default defineComponent({
  name: 'SegmentationBlockOR',

  components: {
    PropertyDeletePanel,
    Dropdown,
    SegmentationSimpleProperty,
    SegmentationComplexProperty,
    OverlayPanel,
    OverrideInfoPanel,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    blockOr: {
      type: Object,
      required: true,
    },

    indexBlockAnd: {
      type: Number,
      required: true,
    },

    indexBlockOr: {
      type: Number,
      required: true,
    },

    lengthBlockAnd: {
      type: Number,
      required: true,
    },

    size: {
      type: String,
      required: false,
      default: '',
    },

    // Optional theme to force specific context (e.g., 'product' for HTML blocks in product loops)
    theme: {
      type: String,
      required: false,
      default: '',
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup(props) {
    const { t } = useI18n();
    // Use theme prop if provided, otherwise use blockOr.theme
    const selectedTheme = ref(props.theme || props.blockOr.theme);
    const refreshPropertyDefinition = ref(0);
    const level = ref(1);
    const errorLevel = ref();
    // Initialize isConditionEditing based on whether the condition is complete

    // Determine available contexts based on theme and template type
    const availableContexts = computed(() => {
      const contexts: string[] = [];

      // Add contact context if theme is 'contacts' or not specified (default)
      if (!selectedTheme.value || selectedTheme.value === 'contacts' || selectedTheme.value === 'contact') {
        contexts.push('contact');
        // Contact theme also includes order and cart contexts
        contexts.push('order', 'cart');
      }

      // Add product context if theme is 'products'
      if (selectedTheme.value === 'products' || selectedTheme.value === 'product') {
        contexts.push('product');
      }

      // Add visitor context only for popup and embed (smart content on site) templates
      const templateType = TemplateEditorState.template?.type;
      if (templateType === TemplateParentTypeEnum.POPUP || templateType === TemplateParentTypeEnum.EMBED) {
        contexts.push('visitor');
      }

      return contexts;
    });

    const selectedProperty = ref(props.blockOr.id ? props.blockOr.id : null);
    const propertiesOptions: any = ref([]);
    const selectedPropertyDefinition = ref<any>({});

    const selectedPropertyGroup = ref(null);
    const overridePanelRef = ref();
    const currentOverrideField = ref<string | null>(null);
    const { loadOverrides, hasOverrides } = useProductOverrides();

    const refreshPropertyComponent = () => {
      refreshPropertyDefinition.value += 1;
    };

    const init = () => {
      const groupProperties: any = store.getters['segmentationsEditor/getGroupProperties']();
      propertiesOptions.value = Object.entries(groupProperties).map(([key, values]) => {
        const groupData = values as any;
        const rawLabel = groupData.rawLabel || false;
        const items = groupData.list || [];
        const mapped = items.map((value: any) => ({
          label: value.rawLabel ? value.label : t(value.label),
          value: value.id,
          isCustomAttribute: value.isCustomAttribute || false,
          isIteration: value.isIteration || false,
          isMetadata: value.isMetadata || false,
        }));
        return {
          label: rawLabel ? groupData.label : t(`segmentations.${key}.label`),
          code: key,
          items: mapped,
        };
      });

      if (selectedProperty.value === null) {
        selectedPropertyDefinition.value = {};
      }

      refreshPropertyComponent();
    };

    const onChangeProperty = () => {
      if (selectedProperty.value) {
        const group = propertiesOptions.value.find((groupItem: any) => groupItem.items.some((item: any) => item.value === selectedProperty.value));
        selectedPropertyGroup.value = group ? group.code : null;
        selectedPropertyDefinition.value = store.getters['segmentationsEditor/getPropertyDefinition'](selectedPropertyGroup.value, selectedProperty.value);
        store.commit('segmentationsEditor/removePropertyError', `${props.indexBlockAnd}-${props.indexBlockOr}`);
      }
      refreshPropertyComponent();
    };

    const deleteBlockOR = () => {
      const { indexBlockAnd, indexBlockOr } = props;
      store.commit('segmentationsEditor/removeBlockOR', {
        indexBlockAND: indexBlockAnd,
        indexBlockOR: indexBlockOr,
      });
    };

    const currentSegmentConfig = computed(() => store.getters['segmentationsEditor/getCurrentSegmentConfig']);

    // Helper function to check if selected option is custom
    const getSelectedOptionIsCustom = (value: string) => propertiesOptions.value.reduce((isCustom: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isCustom || (option?.isCustomAttribute || false);
    }, false);

    // Helper function to check if selected option is an iteration segment
    const getSelectedOptionIsIteration = (value: string) => propertiesOptions.value.reduce((isIteration: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isIteration || (option?.isIteration || false);
    }, false);

    // Helper function to check if selected option is metadata
    const getSelectedOptionIsMetadata = (value: string) => propertiesOptions.value.reduce((isMeta: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isMeta || (option?.isMetadata || false);
    }, false);

    // Helper function to get selected option label
    const getSelectedOptionLabel = (value: string) => propertiesOptions.value.reduce((label: string, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return option ? option.label || '' : label;
    }, '');

    const showEditForm = computed(() => {
      // Never show edit form in readonly mode
      if (props.readonly) {
        return false;
      }
      if (!Object.keys(props.blockOr).length
      || (currentSegmentConfig.value && currentSegmentConfig.value.configProperty.segmentId === props.blockOr.segmentId) || (props.blockOr.sub_properties)
      ) {
        return true;
      }
      // Bare segment: the dropdown is the whole configuration — keep it
      // visible once collapsed, nothing else would render.
      if (
        selectedPropertyDefinition.value
        && !selectedPropertyDefinition.value.sub_properties
        && !selectedPropertyDefinition.value.logical_operators
        && !selectedPropertyDefinition.value.options
      ) {
        return true;
      }
      return false;
    });

    const paddingSize = computed(() => (props.size === 'small' ? '1' : '2'));

    /**
     * Check if a property is overridden
     *
     * @param propertyId - The ID of the property to check
     * @returns true if the property is overridden, false otherwise
     */
    const isPropertyOverridden = (propertyId: string) => {
      // Get the parent context from selectedPropertyGroup
      const parentContextId = selectedPropertyGroup.value || undefined;
      return hasOverrides(propertyId, parentContextId);
    };

    const openOverridePanel = (event: Event, propertyId: string) => {
      currentOverrideField.value = propertyId;
      overridePanelRef.value.toggle(event);
    };

    watch([selectedProperty], () => {
      const property: any = {
        id: selectedProperty.value,
        groupProperty: selectedPropertyGroup.value,
      };

      // Add isCustomAttribute flag if present in definition
      if (selectedProperty.value && selectedPropertyDefinition.value && selectedPropertyDefinition.value.isCustomAttribute) {
        property.isCustomAttribute = true;
      }

      if (selectedProperty.value && selectedPropertyDefinition.value && 'sub_properties' in selectedPropertyDefinition.value) {
        property.sub_properties = [];
      }
      const { indexBlockAnd, indexBlockOr } = props;

      store.commit('segmentationsEditor/updateSegmentProperty', {
        indexBlockAND: indexBlockAnd,
        indexBlockOR: indexBlockOr,
        segmentData: property,
      });
      if (selectedPropertyDefinition.value && !selectedPropertyDefinition.value.sub_properties) {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', {
          configProperty: property,
          propertyDefinition: selectedPropertyDefinition.value,
        });
      }
      if (selectedPropertyDefinition.value.sub_properties) {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    });

    watch(() => store.getters['segmentationsEditor/getErrors'], () => {
      const errorsArray = store.getters['segmentationsEditor/getErrors'];
      errorLevel.value = null;
      if (errorLevel.value === null) {
        errorsArray.properties.forEach((errorLevelString: string) => {
          if (errorLevelString === `${props.indexBlockAnd}-${props.indexBlockOr}`) {
            errorLevel.value = 'property';
          }
        });
      }
    }, { deep: true });

    const dropdownRef = ref();

    const onBeforeShow = async () => {
      if (currentSegmentConfig.value) {
        if (((Object.keys(props.blockOr).length && currentSegmentConfig.value.configProperty.segmentId !== props.blockOr.segmentId)) || !Object.keys(props.blockOr).length) {
          const { segmentId } = currentSegmentConfig.value.configProperty;
          const validation = await validateSegmentProperty(currentSegmentConfig.value.configProperty, currentSegmentConfig.value.propertyDefinition);
          if (validation && !validation.success) {
            store.commit('segmentationsEditor/pushError', { type: 'segments', value: { segmentId, value: validation.validate.value } });
            dropdownRef.value.hide();
            return false;
          }
          store.commit('segmentationsEditor/removeError', { type: 'segments', id: 'segmentId', value: segmentId });
          store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
          store.commit('segmentationsEditor/setRefreshContactsNumber', true);
        }
      } else {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    };

    onMounted(async () => {
      if (!props.readonly) {
        await loadOverrides();
      }
      init();
      onChangeProperty();
    });

    return {
      t,
      deleteBlockOR,
      selectedTheme,
      availableContexts,
      onChangeProperty,
      selectedProperty,
      selectedPropertyDefinition,
      propertiesOptions,
      level,
      errorLevel,
      refreshPropertyDefinition,
      refreshPropertyComponent,
      showEditForm,
      onBeforeShow,
      dropdownRef,
      paddingSize,
      getSelectedOptionIsCustom,
      getSelectedOptionIsIteration,
      getSelectedOptionIsMetadata,
      getSelectedOptionLabel,
      isPropertyOverridden,
      overridePanelRef,
      openOverridePanel,
      currentOverrideField,
      readonly: props.readonly,
    };
  },
});
